import { useState } from 'react';
import { Box, Button, IconButton, Drawer } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import { Link } from 'react-router-dom';

/** The INCRYPT RPG on the Epic Games Store — opens in a new tab. */
export const RPG_URL =
  'https://store.epicgames.com/p/incrypt-shadows-of-satoshi-34602b';

// Circle "C" emblem — the same asset the member-portal header uses — paired with
// the INCRYPT wordmark rendered as white letter-spaced text (see the header below).
const LOGO_ICON =
  'https://incrypt-public-assets-global.s3.ap-southeast-2.amazonaws.com/images/logo-menu.png';

type NavItem = { label: string; to: string; external?: boolean };

const NAV: NavItem[] = [
  { label: 'Giveaway', to: '/giveaway' },
  // this week's promo pack, beside the giveaway its purchase enters
  { label: 'Promo Pack', to: '/promo' },
  { label: 'RPG', to: RPG_URL, external: true },
  // sits next to the RPG because the two games are the pair a visitor is choosing between;
  // internal, unlike RPG, since the card game lives here rather than on the Epic store
  { label: 'TCG', to: '/trading-card-game' },
  { label: 'Digital Collectibles', to: '/digital-collectibles' },
  { label: 'Physical Cards', to: '/physical-cards' },
  { label: 'Contact Us', to: '/contact-us' }
];

const loginBtnSx = {
  px: { xs: 2, md: 2.6 },
  py: { xs: 0.7, md: 0.9 },
  borderRadius: 999,
  fontWeight: 800,
  fontSize: { xs: 13, md: 14 },
  textTransform: 'none' as const,
  letterSpacing: '0.01em',
  color: '#04121a',
  background: 'linear-gradient(180deg,#6cf0ff,#38e8ff)',
  boxShadow: '0 10px 26px -10px rgba(56,232,255,0.75)',
  '&:hover': { background: 'linear-gradient(180deg,#8af5ff,#4cecff)' }
};

/** One nav entry — an external anchor (new tab) or an in-app router link. */
const NavLink = ({
  item,
  onClick,
  block
}: {
  item: NavItem;
  onClick?: () => void;
  block?: boolean;
}) => {
  const common = {
    onClick,
    sx: {
      display: block ? 'block' : 'inline-flex',
      alignItems: 'center',
      color: '#c7d6e0',
      fontWeight: 600,
      fontSize: block ? 17 : 15,
      letterSpacing: '0.01em',
      textDecoration: 'none',
      py: block ? 1.25 : 0,
      transition: 'color 160ms ease',
      '&:hover': { color: '#38e8ff' }
    }
  };
  return item.external ? (
    <Box
      component="a"
      href={item.to}
      target="_blank"
      rel="noopener noreferrer"
      {...common}
    >
      {item.label}
    </Box>
  ) : (
    <Box component={Link} to={item.to} {...common}>
      {item.label}
    </Box>
  );
};

/** Sticky, translucent top bar: brand logo, primary nav and a Log In CTA.
    Collapses to a right-hand drawer under lg: the seven links and Log In need
    about 1090px on one line, so between md and that the labels wrapped onto two
    lines (from 900 to 950px even before Promo Pack joined the list). */
const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        background: 'rgba(6,11,18,0.72)',
        borderBottom: '1px solid rgba(140,170,190,0.12)'
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          height: { xs: 62, md: 74 },
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Box
          component={Link}
          to="/"
          aria-label="Incrypt home"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.1,
            mr: 'auto',
            textDecoration: 'none'
          }}
        >
          <Box
            component="img"
            src={LOGO_ICON}
            alt=""
            sx={{ height: { xs: 30, md: 38 }, width: 'auto', display: 'block' }}
          />
          <Box
            component="span"
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: { xs: 18, md: 22 },
              letterSpacing: '0.16em',
              lineHeight: 1
            }}
          >
            INCRYPT
          </Box>
        </Box>

        {/* Desktop nav */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            gap: { md: 2.5, lg: 3.25 }
          }}
        >
          {NAV.map((item) => (
            <NavLink key={item.label} item={item} />
          ))}
          <Button
            component={Link}
            to="/login"
            startIcon={<LoginIcon sx={{ fontSize: 18 }} />}
            sx={loginBtnSx}
          >
            Log In
          </Button>
        </Box>

        {/* Mobile: keep Log In visible, collapse the rest */}
        <Box
          sx={{
            display: { xs: 'flex', lg: 'none' },
            alignItems: 'center',
            gap: 1
          }}
        >
          <Button component={Link} to="/login" size="small" sx={loginBtnSx}>
            Log In
          </Button>
          <IconButton
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            sx={{ color: '#e8f4fb' }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={close}
        PaperProps={{
          sx: {
            width: 264,
            background: '#0a0f16',
            borderLeft: '1px solid rgba(140,170,190,0.14)',
            color: '#e8f4fb',
            px: 2.5,
            py: 2
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            aria-label="Close menu"
            onClick={close}
            sx={{ color: '#8ea3b3' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column' }}>
          {NAV.map((item) => (
            <NavLink key={item.label} item={item} onClick={close} block />
          ))}
          <Button
            component={Link}
            to="/login"
            onClick={close}
            startIcon={<LoginIcon sx={{ fontSize: 18 }} />}
            sx={{ ...loginBtnSx, mt: 2, py: 1.1 }}
          >
            Log In
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SiteHeader;
